import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import PostAuthRedirect from "@/components/PostAuthRedirect";
import ScrollToTop from "@/components/ScrollToTop";
import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";
import Privacy from "./pages/Privacy.tsx";
import Auth from "./pages/Auth.tsx";
import AdminAthletes from "./pages/admin/AdminAthletes.tsx";
import AdminAthleteOverview from "./pages/admin/AdminAthleteOverview.tsx";
import AdminDashboardStates from "./pages/admin/AdminDashboardStates.tsx";
import AdminProfileDraft from "./pages/admin/AdminProfileDraft.tsx";
import AdminDraftReviewPreview from "./pages/admin/AdminDraftReviewPreview.tsx";
import AthleteQuickStart from "./pages/AthleteQuickStart.tsx";
import ClaimProfile from "./pages/ClaimProfile.tsx";
import AdminCauses from "./pages/admin/AdminCauses.tsx";
import AdminProducts from "./pages/admin/AdminProducts.tsx";
import AdminTeam from "./pages/admin/AdminTeam.tsx";
import AdminContent from "./pages/admin/AdminContent.tsx";
import AdminNews from "./pages/admin/AdminNews.tsx";
import AdminScores from "./pages/admin/AdminScores.tsx";
import News from "./pages/News.tsx";
import AthleteProfile from "./pages/AthleteProfile.tsx";
import ProductDetail from "./pages/ProductDetail.tsx";
import CauseDetail from "./pages/CauseDetail.tsx";
import Shop from "./pages/Shop.tsx";
import Causes from "./pages/Causes.tsx";
import Contact from "./pages/Contact.tsx";
import Athletes from "./pages/Athletes.tsx";
import Teams from "./pages/Teams.tsx";
import Wave from "./pages/Wave.tsx";
import WaveQrRedirect from "./pages/WaveQrRedirect.tsx";

import TeamDetail from "./pages/TeamDetail.tsx";
import AdminTeams from "./pages/admin/AdminTeams.tsx";
import AdminBlog from "./pages/admin/AdminBlog.tsx";
import Spotlight from "./pages/Spotlight.tsx";
import SpotlightPost from "./pages/SpotlightPost.tsx";
import BecomeAthlete from "./pages/BecomeAthlete.tsx";
import AthleteDashboard from "./pages/AthleteDashboard.tsx";
import Dashboard from "./pages/Dashboard.tsx";
import SetPassword from "./pages/SetPassword.tsx";
import ForgotPassword from "./pages/ForgotPassword.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import AdminSignups from "./pages/admin/AdminSignups.tsx";
import AdminFans from "./pages/admin/AdminFans.tsx";
import AdminReview from "./pages/admin/AdminReview.tsx";
import Scores from "./pages/Scores.tsx";
import MediaHub from "./pages/MediaHub.tsx";
import MediaCategory from "./pages/MediaCategory.tsx";
import MediaStory from "./pages/MediaStory.tsx";
import AdminMedia from "./pages/admin/AdminMedia.tsx";
import AdminSupport from "./pages/admin/AdminSupport.tsx";
import AdminPreview from "./pages/admin/AdminPreview.tsx";
import AdminBulkImport from "./pages/admin/AdminBulkImport.tsx";
import AdminQr from "./pages/admin/AdminQr.tsx";
import AdminUpdates from "./pages/admin/AdminUpdates.tsx";
import AdminOrders from "./pages/admin/AdminOrders.tsx";
import AthleteProfileDemo from "./pages/AthleteProfileDemo.tsx";
import AthleteBusinessHubDemo from "./pages/AthleteBusinessHubDemo.tsx";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <PostAuthRedirect />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/news" element={<News />} />
            <Route path="/scores" element={<Scores />} />
            <Route path="/athletes" element={<Athletes />} />
            <Route path="/teams" element={<Teams />} />
            <Route path="/wave" element={<Wave />} />
            <Route path="/q/wave" element={<WaveQrRedirect kind="campaign" />} />
            <Route path="/q/wave/roster" element={<WaveQrRedirect kind="roster" />} />
            <Route path="/q/wave/athlete/:code" element={<WaveQrRedirect kind="athlete" />} />

            <Route path="/team/:slug" element={<TeamDetail />} />
            <Route path="/teams/:slug" element={<TeamDetail />} />
            <Route path="/admin/teams" element={<AdminTeams />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/causes" element={<Causes />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/athlete-profile-demo" element={<AthleteProfileDemo />} />
            <Route path="/athlete-business-hub-demo" element={<AthleteBusinessHubDemo />} />
            <Route path="/athlete/:slug" element={<AthleteProfile />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/cause/:id" element={<CauseDetail />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/athlete-login" element={<Auth defaultAudience="athlete" />} />
            <Route path="/spotlight" element={<Spotlight />} />
            <Route path="/spotlight/:slug" element={<SpotlightPost />} />
            <Route path="/become-an-athlete" element={<BecomeAthlete />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/admin/signups" element={<AdminSignups />} />
            <Route path="/admin/fans" element={<AdminFans />} />
            <Route path="/admin/review" element={<AdminReview />} />
            <Route path="/admin/news" element={<AdminNews />} />
            <Route path="/admin/scores" element={<AdminScores />} />
            <Route path="/admin/blog" element={<AdminBlog />} />
            <Route path="/media-hub" element={<MediaHub />} />
            <Route path="/media-hub/story/:slug" element={<MediaStory />} />
            <Route path="/media-hub/:category" element={<MediaCategory />} />
            <Route path="/admin/media" element={<AdminMedia />} />
            <Route path="/admin/support" element={<AdminSupport />} />
            <Route path="/admin/preview" element={<AdminPreview />} />
            <Route path="/admin/bulk-import" element={<AdminBulkImport />} />
            <Route path="/admin/import" element={<AdminBulkImport />} />
            <Route path="/admin/qr" element={<AdminQr />} />
            <Route path="/admin/updates" element={<AdminUpdates />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route path="/admin/athletes/overview" element={<AdminAthleteOverview />} />
            <Route path="/admin/athletes/:id/draft" element={<AdminProfileDraft />} />
            <Route path="/admin/preview/dashboard-states" element={<AdminDashboardStates />} />
            <Route path="/admin/preview/draft-review" element={<AdminDraftReviewPreview />} />
            <Route path="/dashboard/quick-start" element={<AthleteQuickStart />} />
            <Route path="/claim" element={<ClaimProfile />} />

            <Route path="/admin" element={<AdminAthletes />} />
            <Route path="/admin/causes" element={<AdminCauses />} />
            <Route path="/admin/products" element={<AdminProducts />} />
            <Route path="/admin/team" element={<AdminTeam />} />
            <Route path="/admin/content" element={<AdminContent />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
